import React, {FunctionComponent, memo, useEffect, useMemo} from 'react'
import styles from './NotificationPanel.module.sass'
import { notificationActions, notificationActions as actions } from '../../../../redux/global/notification'
import { useBoolean, useRootDispatch, useRootSelector } from '../../../../utils/hooks'
import Badge from '../../../etc/Badge/Badge'
import Button from '../../../controls/Button/Button'
import SidePanel from '../../../etc/SidePanel/SidePanel'
import { conditionCons, entries, pick } from '../../../../utils/utils'
import { flow } from 'fp-ts/es6/function'
import BellIcon from '../../../../../files/subscription/subscribe.svg'
import CommentIcon from '../../../../../files/comment/comment.svg'
import ReadAllIcon from '../../../../../files/comment/read-all.svg'
import { groupBy, NonEmptyArray } from 'fp-ts/es6/NonEmptyArray'
import { MessageType, Notification, NotificationType } from '../../../../responses/notification'
import { pipe } from 'fp-ts/es6/pipeable'
import { parseDate, toDateString, toServerDate } from '../../../../utils/date-utils'
import NotificationElement from '../../../data-components/elements/NotificationElement/NotificationElement'
import { isToday, isYesterday } from 'date-fns'
import { reverse, sort } from 'fp-ts/es6/Array'
import { RequestStatus } from '../../../../responses/response'
import { contramap, ordString } from 'fp-ts/es6/Ord'
import { AnimatePresence } from 'framer-motion'
import { MESSAGES } from '../../../../constants/messages'
import { Colors } from '../../../../styles'
import { GroupedVirtuoso } from 'react-virtuoso'
import Skeleton from 'react-loading-skeleton'
import { isEmpty } from 'fp-ts/Array'
import { snd } from 'fp-ts/Tuple'
import { head } from 'fp-ts/NonEmptyArray'
import { shallowEqual } from 'react-redux'
import Spinner from '../../../etc/Spinner/Spinner'
import Tooltip from '../../../etc/Tooltip'
import DoubleSwitch from '../../../controls/DoubleSwitch/DoubleSwitch'
import NotificationOutboxElement from '../../../data-components/elements/NotificationElement/NotificationOutboxElement'


const tupleOrd = contramap<string, [string, NonEmptyArray<Notification>]>(([date]) => date)(ordString)

export const groupNotifications = flow(
    groupBy((notification: Notification) => pipe(notification.created_at, parseDate, toServerDate)),
    entries,
    sort(tupleOrd),
    reverse,
)

const createUpdateNotification = (updateAt: string): Notification => ({
    id: 0,
    kind: 'system',
    read: false,
    type: NotificationType.SYSTEM_MESSAGE,
    created_at: (new Date()).toISOString(),
    data: {
        type: MessageType.ERROR,
        subject: 'Обновление системы',
        text: MESSAGES.UPDATE_WARNING(updateAt),
        url: ''
    },
})

const NotificationPanel: FunctionComponent = () => {
    const dispatch = useRootDispatch()
    const {state: open, setTrue, setFalse} = useBoolean(false)

    const {filter, status, readStatus, outbox} = useRootSelector(state => pick(state.global.notification, 'filter', 'status', 'readStatus', 'outbox'), shallowEqual)

    const resources = useRootSelector(state => state.global.history.resources)
    const unread = useRootSelector(state => state.global.system.updateAt ?
        state.global.notification.unread + 1:
        state.global.notification.unread
    )
    const data = useRootSelector(state => {
        const updateDate = state.global.system.updateAt
        return conditionCons(!!updateDate, createUpdateNotification(updateDate))(state.global.notification.data)
    })

    const memoized = useMemo(() => groupNotifications(data), [data])

    useEffect(() => {
        (open && status === RequestStatus.INITIAL) && dispatch(actions.data.init(null))
    }, [dispatch, open, status])

    const arrayCountsGroups = useMemo(() => memoized.map(arr => arr[1].length), [memoized])



    return <Badge badge={Math.max(unread, 0)}>
        <Button
            // todo решить с исключением
            // intent={Intent.DARK}
            title={'Уведомления'}
            onClick={setTrue}
        >
            <BellIcon/>
        </Button>
        <AnimatePresence>
            {
                open && <SidePanel
                    className={styles.panel}
                    onClose={setFalse}
                    header={<>
                        <BellIcon className={styles.icon}/>
                        <div>Уведомления</div>
                        <Button
                            title={filter === 'user' ? 'Показывать все уведомления' : 'Показывать только комментарии'}
                            color={filter === 'user' ? Colors.blue : Colors.grey}
                            onClick={() => dispatch(notificationActions.data.init(filter === 'user' ? null : 'user'))}
                        >
                            <CommentIcon/>
                        </Button>
                        {
                            readStatus === RequestStatus.FETCHING?
                                <Tooltip title={'Выполняется прочтение'}><Spinner/></Tooltip>:
                                <Button
                                    title={'Прочитать все'}
                                    color={unread > 0 ? Colors.orange : Colors.grey}
                                    onClick={() => dispatch(notificationActions.readAll.request())}
                                >
                                    <ReadAllIcon/>
                                </Button>
                        }

                    </>}
                >
                    <div className={styles.switch_block}>
                        <DoubleSwitch
                            className={styles.switch}
                            value={ outbox != 1 ? false : true }
                            onChange={ () => {
                                dispatch(notificationActions.setOutbox(outbox === 1 ? 0 : 1))
                                dispatch(notificationActions.data.init(filter === 'user' ? 'user' : null))
                            }}
                            left={'Входящие'}
                            right={'Исходящие'}
                            leftColor={Colors.darkBlue}
                            rightColor={Colors.green}
                            notification={true}
                        />
                    </div>

                    {
                        (isEmpty(data) && status === RequestStatus.FETCHING) &&
                        <Skeleton height={100} count={8}/>
                    }
                    {outbox === 0 || outbox === null ?
                        <GroupedVirtuoso
                            groupCounts={arrayCountsGroups}
                            endReached={() => dispatch(notificationActions.data.more())}
                            groupContent={(index) => {
                                const date = parseDate(pipe(memoized[index], snd, head).created_at) || new Date()
                                return <div className={styles.date}>
                                    <div className={styles.content}>
                                        {isToday(date) && <>Сегодня | </>}
                                        {isYesterday(date) && <>Вчера | </>}
                                        {pipe(memoized[index][0], parseDate, toDateString)}
                                    </div>
                                </div>
                            }}
                            itemContent={(index, groupIndex) => {
                                const notification: Notification | undefined = data[index]
                                return (
                                    notification ? <NotificationElement
                                        key={notification.id}
                                        type={'notification'}
                                        notification={notification}
                                        resources={resources}
                                        onRead={flow(notificationActions.readOne.request, dispatch)}
                                    /> : <div>
                                        {groupIndex} {index} {JSON.stringify(memoized[groupIndex][1])}
                                    </div>
                                )
                            }}
                        />
                    : <GroupedVirtuoso
                            groupCounts={arrayCountsGroups}
                            endReached={() => dispatch(notificationActions.data.more())}
                            groupContent={(index) => {
                                const date = parseDate(pipe(memoized[index], snd, head).created_at) || new Date()
                                return <div className={styles.date}>
                                    <div className={styles.content}>
                                        {isToday(date) && <>Сегодня | </>}
                                        {isYesterday(date) && <>Вчера | </>}
                                        {pipe(memoized[index][0], parseDate, toDateString)}
                                    </div>
                                </div>
                            }}
                            itemContent={(index, groupIndex) => {
                                const notification: Notification | undefined = data[index]
                                return (
                                    notification ? <NotificationOutboxElement
                                        key={notification.id}
                                        type={'notification'}
                                        notification={notification}
                                        resources={resources}
                                        onRead={()=>{console.log()}}
                                    /> : <div>
                                        {groupIndex} {index} {JSON.stringify(memoized[groupIndex][1])}
                                    </div>
                                )
                            }}
                        />
                    }

                </SidePanel>
            }
        </AnimatePresence>
    </Badge>
}

NotificationPanel.whyDidYouRender = true

export default memo(NotificationPanel)